/**
 * Particle System
 * Handles particle pool, creation, and animation
 *
 * Note: Uses Web Animations API for all animations.
 * No GSAP dependency - pure browser APIs for better performance.
 */

import { logger } from '@/utils/logger.js';
import type { EffectElements, ColorPalettes, Mood } from './types.js';

/**
 * Create single particle element
 */
export function createParticle(effectElements: EffectElements): HTMLElement | null {
  try {
    if (!effectElements.particles) {
      logger.error('❌ Cannot create particle: container not available');
      return null;
    }

    const particle = document.createElement('div');
    particle.className = 'beat-particle';
    particle.style.cssText = `
      position: absolute;
      width: 6px;
      height: 6px;
      background: #ffffff;
      border-radius: 50%;
      pointer-events: none;
      opacity: 0;
      z-index: 1002;
      box-shadow: 0 0 4px rgba(255, 255, 255, 0.8);
    `;

    effectElements.particles.appendChild(particle);
    return particle;
  } catch (error) {
    logger.error('❌ Failed to create particle:', error);
    return null;
  }
}

/**
 * Initialize particle pool
 */
export function initializeParticlePool(
  effectElements: EffectElements,
  particlePool: HTMLElement[],
  maxParticles: number
): void {
  logger.info(`🎨 initializeParticlePool called - container exists: ${!!effectElements.particles}, maxParticles: ${maxParticles}`);

  if (!effectElements.particles) {
    logger.warn('⚠️ Particle container not created, skipping particle system initialization');
    return;
  }

  // Clear existing particles
  particlePool.length = 0;

  try {
    let successCount = 0;
    for (let i = 0; i < maxParticles; i++) {
      const particle = createParticle(effectElements);
      if (particle) {
        particlePool.push(particle);
        successCount++;
      } else {
        logger.error(`❌ Failed to create particle ${i}`);
      }
    }
    logger.info(`✅ Created ${successCount}/${maxParticles} particles, pool size: ${particlePool.length}`);
  } catch (error) {
    logger.error('❌ Particle system initialization failed:', error);
  }
}

/**
 * Animate single particle
 */
export function animateParticle(
  particle: HTMLElement,
  intensity: number,
  colors: string[],
  effectElements: EffectElements,
  returnParticleToPool: (particle: HTMLElement) => void
): void {
  const color = colors[Math.floor(Math.random() * colors.length)];

  if (!effectElements.particles) {
    logger.error('❌ Particle container not available during animation');
    return;
  }

  const containerWidth = effectElements.particles.offsetWidth || 400;
  const containerHeight = effectElements.particles.offsetHeight || 600;

  const startX = Math.random() * containerWidth;
  const startY = Math.random() * containerHeight;

  // Random direction and velocity
  const angle = Math.random() * Math.PI * 2;
  const velocity = 50 + (intensity * 100);
  const endX = startX + Math.cos(angle) * velocity;
  const endY = startY + Math.sin(angle) * velocity;

  // Particle style
  particle.style.background = color;
  particle.style.left = startX + 'px';
  particle.style.top = startY + 'px';
  particle.style.transform = 'scale(1)';
  particle.style.width = '8px';
  particle.style.height = '8px';
  particle.style.zIndex = '1001';
  particle.style.boxShadow = `0 0 10px ${color}`;

  // Use Web Animations API
  const durationMs = 1000 + (Math.random() * 500);
  const deltaX = endX - startX;
  const deltaY = endY - startY;

  const anim = particle.animate([
    {
      opacity: 1,
      transform: 'translate(0, 0) scale(1)'
    },
    {
      opacity: 0,
      transform: `translate(${deltaX}px, ${deltaY}px) scale(0.1)`
    }
  ], {
    duration: durationMs,
    easing: 'cubic-bezier(0.22, 1, 0.36, 1)', // power2.out equivalent
    fill: 'forwards'
  });

  anim.onfinish = () => returnParticleToPool(particle);
}

/**
 * Return particle to pool
 */
export function returnParticleToPool(
  particle: HTMLElement,
  activeParticles: HTMLElement[],
  particlePool: HTMLElement[]
): void {
  particle.style.opacity = '0';
  particle.style.transform = 'scale(1) translate(0, 0)';

  const index = activeParticles.indexOf(particle);
  if (index > -1) {
    activeParticles.splice(index, 1);
    particlePool.push(particle);
  }
}

/**
 * Trigger particle explosion
 */
export function triggerParticleExplosion(
  intensity: number,
  mood: Mood,
  effectElements: EffectElements,
  particlePool: HTMLElement[],
  activeParticles: HTMLElement[],
  colorPalettes: ColorPalettes,
  animateParticle: (particle: HTMLElement, intensity: number, colors: string[]) => void,
  initializeParticleSystem: () => void
): void {
  if (!effectElements.particles) {
    logger.warn('⚠️ Particle container not available');
    return;
  }

  if (particlePool.length === 0) {
    logger.warn('⚠️ Particle pool is empty, attempting to initialize...');
    initializeParticleSystem();

    if (particlePool.length === 0) {
      logger.error('❌ Failed to initialize particle pool');
      return;
    }
  }

  const particleCount = Math.floor(5 + (intensity * 15));
  const colors = colorPalettes[mood] || colorPalettes.normal;

  for (let i = 0; i < particleCount && particlePool.length > 0; i++) {
    const particle = particlePool.pop();
    if (!particle) {
      logger.warn(`⚠️ No particle available at index ${i}`);
      break;
    }

    activeParticles.push(particle);
    animateParticle(particle, intensity, colors);
  }
}

/**
 * Animate sparkle particle (for hihat)
 */
export function animateSparkleParticle(
  particle: HTMLElement,
  _intensity: number,
  effectElements: EffectElements,
  returnParticleToPool: (particle: HTMLElement) => void
): void {
  if (!effectElements.particles) return;

  const containerWidth = effectElements.particles.offsetWidth || 400;
  const containerHeight = effectElements.particles.offsetHeight || 600;

  const startX = Math.random() * containerWidth;
  const startY = Math.random() * containerHeight;

  particle.style.background = '#ffffff';
  particle.style.left = startX + 'px';
  particle.style.top = startY + 'px';
  particle.style.width = '2px';
  particle.style.height = '2px';

  // Use Web Animations API
  const anim = particle.animate([
    { opacity: 1, transform: 'scale(1)' },
    { opacity: 0, transform: 'scale(0.1)' }
  ], {
    duration: 500,
    easing: 'cubic-bezier(0.22, 1, 0.36, 1)', // power2.out equivalent
    fill: 'forwards'
  });

  anim.onfinish = () => returnParticleToPool(particle);
}

/**
 * Trigger subtle sparkle (for hihat)
 */
export function triggerSubtleSparkle(
  intensity: number,
  particlePool: HTMLElement[],
  activeParticles: HTMLElement[],
  _effectElements: EffectElements,
  animateSparkleParticle: (particle: HTMLElement, intensity: number) => void
): void {
  const particleCount = Math.floor(2 + intensity * 5);

  for (let i = 0; i < particleCount && particlePool.length > 0; i++) {
    const particle = particlePool.pop();
    if (!particle) break;

    activeParticles.push(particle);
    animateSparkleParticle(particle, intensity);
  }
}
